import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/solid';
import { syncronizeOrders, trackOrders, importOrders, generateTickets, updateOrderStatus } from '../orders-controller/order-controller';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import BatchPrint from './order_table_components/BatchPrint';
import * as XLSX from 'xlsx';
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';


const OrdersHeader = ({
    exportToExcel,
    filteredOrders,
    searchTerm,
    statusFilter,
    selectedOrders = [],
    setSelectedOrders = () => { },
    refreshOrders = () => { },
    handleTrackOrders = () => { },
    handleSyncOrders = () => { },
    standAloneSyncOrders = false,
    standAloneTrackOrders = false,
    standAlonePrintOrders = false,
    handlePrintOrders,
    refreshData,     // Add refreshData prop for table refresh
    refreshDashboard,
    pageTitle = "Commandes",
    addNewRoute = "/admin/orders/new",
    addNewLabel = "Nouvelle Commande",
    isExchange = false // Add this parameter with default value false
}) => {
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
    const [syncMode, setSyncMode] = useState('selected'); // 'selected' or 'all'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPrintModalOpen, setPrintModalOpen] = useState(false);
    const [printMode, setPrintMode] = useState('selected'); // 'selected' or 'all'
    const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
    const [trackMode, setTrackMode] = useState('selected'); // 'selected' or 'all'
    const [isTrackingSubmitting, setIsTrackingSubmitting] = useState(false); // Add a new state variable for tracking submission
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importPreview, setImportPreview] = useState([]);
    const [isGeneratingTickets, setIsGeneratingTickets] = useState(false);
    const [isMarkingPaid, setIsMarkingPaid] = useState(false);
    const {hasPermission} = usePermissionViewModel('commande')


    const navigate = useNavigate();

    const isDeliveredFilter = String(statusFilter || '').toLowerCase() === 'delivered';

    const handleMarkAsPaid = async () => {
        if (!selectedOrders || selectedOrders.length === 0) {
            toast.warning("Veuillez sélectionner au moins une commande livrée.");
            return;
        }
        const ids = selectedOrders
            .map(o => (typeof o === 'object' && o !== null ? o.id : o))
            .filter(id => id !== undefined && id !== null);
        if (ids.length === 0) {
            toast.warning("Aucune commande valide sélectionnée.");
            return;
        }
        if (!window.confirm(`Marquer ${ids.length} commande(s) livrée(s) comme payée(s) ?`)) return;
        setIsMarkingPaid(true);
        try {
            await updateOrderStatus(null, 'paid', ids);
            toast.success(`${ids.length} commande(s) marquée(s) comme payée(s).`);
            setSelectedOrders([]);
            if (typeof refreshOrders === 'function') refreshOrders();
            if (typeof refreshData === 'function') refreshData();
            if (typeof refreshDashboard === 'function') refreshDashboard();
        } catch (err) {
            console.error('Mark as paid failed:', err);
            toast.error("Erreur lors du marquage comme payé.");
        } finally {
            setIsMarkingPaid(false);
        }
    };

    const openSyncModal = (mode) => {
        setSyncMode(mode);
        setIsSyncModalOpen(true);
    };

    const openPrintModal = (mode) => {
        setPrintMode(mode);
        setPrintModalOpen(true);
    };

    // Add new function to open tracking modal
    const openTrackModal = (mode) => {
        setTrackMode(mode);
        setIsTrackModalOpen(true);
    };

    // Add this function to open the import modal
    const openImportModal = () => {
        setIsImportModalOpen(true);
        setSelectedFile(null);
        setImportPreview([]);
    };

    // Handle generating tickets PDF
    const handleGenerateTickets = async () => {
        if (selectedOrders.length === 0) {
            toast.warning("Veuillez sélectionner au moins une commande.");
            return;
        }
        setIsGeneratingTickets(true);
        try {
            const orderIds = selectedOrders.map(order => order.id);
            await generateTickets(orderIds);
            toast.success(`Ticket(s) généré(s) pour ${orderIds.length} commande(s)`);
        } catch (error) {
            console.error("Error generating tickets:", error);
            toast.error("Erreur lors de la génération des tickets.");
        } finally {
            setIsGeneratingTickets(false);
        }
    };

    // Update the handleConfirmTracking function to call the API
    // Update handleConfirmTracking function to use prop if available

    // Update the handleConfirmTracking function
    const handleConfirmTracking = async () => {
        // If a custom tracking handler was provided, use it
        if (standAloneTrackOrders) {
            // Call the provided tracking handler with the appropriate parameters
            handleTrackOrders(trackMode, trackMode === 'selected' ? selectedOrders : filteredOrders);
            setIsTrackModalOpen(false);
            return;
        }

        // Get the orders to track
        const ordersToTrack = trackMode === 'selected'
            ? selectedOrders
            : filteredOrders;

        if (ordersToTrack.length === 0) {
            toast.warning("Aucune commande à suivre.");
            setIsTrackModalOpen(false);
            return;
        }

        try {
            // Show loading state
            setIsTrackingSubmitting(true);

            // Extract order IDs to send to the API
            const orderIds = ordersToTrack.map(order => order.id);

            // Call the tracking API
            await trackOrders(orderIds);

            // Show success message
            toast.success(`${ordersToTrack.length} commande(s) en cours de suivi`);

            // If tracking was for selected orders, optionally clear the selection
            if (trackMode === 'selected') {
                setSelectedOrders([]);
            }

            // Use provided refresh functions if available, otherwise use defaults
            if (refreshData) {
                refreshData();
            } else {
                refreshOrders();
            }

            if (refreshDashboard) {
                refreshDashboard();
            }

            console.log(`Successfully tracked ${ordersToTrack.length} orders`);
        } catch (error) {
            console.error("Tracking error:", error);
            toast.error("Échec du suivi des commandes. Veuillez réessayer.");
        } finally {
            setIsTrackingSubmitting(false);
            setIsTrackModalOpen(false);
        }
    };

    // Modified handleSynchronize function
    const handleSynchronize = async () => {
        // If a custom sync handler was provided, use it
        if (standAloneSyncOrders) {
            console.log("Selected Orders xddddddddd:", selectedOrders);
            // Call the provided sync handler with the appropriate parameters
            handleSyncOrders(syncMode, syncMode === 'selected' ? selectedOrders : filteredOrders);
            setIsSyncModalOpen(false);
            return;
        }

        // Otherwise use the default implementation
        setIsSubmitting(true);
        try {
            // Determine which orders to synchronize
            let orderIds = [];

            if (syncMode === 'selected') {
                // For selected orders, just extract the IDs
                orderIds = selectedOrders.map(order => order.id);
            } else {
                // For filtered orders, only include those with status 'confirmed' or 'readyToShip'
                orderIds = filteredOrders
                    .filter(order => {
                        const status = order.etat?.toLowerCase();
                        return status === 'confirmed' ||
                            status === 'confirmé' ||
                            status === 'readytoship' ||
                            status === 'ready to ship' ||
                            status === 'prêt à expédier';
                    })
                    .map(order => order.id);
            }

            if (orderIds.length === 0) {
                toast.warning(syncMode === 'selected'
                    ? "Aucune commande sélectionnée à synchroniser"
                    : "Aucune commande éligible à synchroniser (confirmée ou prête à expédier)");
                setIsSyncModalOpen(false);
                return;
            }

            // Call the API to synchronize orders
            await syncronizeOrders(orderIds);

            toast.success(`${orderIds.length} commande(s) synchronisée(s) avec succès!`);

            // If you want to clear selections after sync
            if (syncMode === 'selected') {
                setSelectedOrders([]);
            }
            // Use provided refresh functions if available, otherwise use defaults
            if (refreshData) {
                refreshData();
            } else {
                refreshOrders();
            }

            if (refreshDashboard) {
                refreshDashboard();
            }
        } catch (error) {
            console.error("Synchronization error:", error);
            toast.error("Échec de la synchronisation des commandes. Veuillez réessayer.");
        } finally {
            setIsSubmitting(false);
            setIsSyncModalOpen(false);
        }
    };

    // Update the handleBatchPrint function to properly handle individual order exchange status

    const handleBatchPrint = () => {
        if (standAlonePrintOrders) {
            // Call the provided print handler with the appropriate parameters
            handlePrintOrders(printMode, printMode === 'selected' ? selectedOrders : filteredOrders);
            setPrintModalOpen(false);
            return;
        }

        const ordersToPrint = printMode === 'selected'
            ? selectedOrders
            : filteredOrders;

        if (ordersToPrint.length === 0) {
            toast.warning("Aucune commande à imprimer.");
            setPrintModalOpen(false);
            return;
        }

        // Show a loading toast
        const loadingToastId = toast.info(
            `Préparation de ${ordersToPrint.length} étiquettes...`,
            { autoClose: false }
        );

        // Create a new window for printing
        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            toast.error("Veuillez autoriser les fenêtres pop-up pour l'impression");
            toast.dismiss(loadingToastId);
            setPrintModalOpen(false);
            return;
        }

        // Start with a basic loading indicator in the print window
        printWindow.document.write(`
    <html>
        <head>
            <title>Étiquettes commandes (${ordersToPrint.length})</title>
            <style>
                body {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                    font-family: Arial, sans-serif;
                }
                .loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100vh;
                    width: 100%;
                }
                .spinner {
                    border: 5px solid #f3f3f3;
                    border-top: 5px solid #3498db;
                    border-radius: 50%;
                    width: 50px;
                    height: 50px;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @media print {
                    .loading, .loading * {
                        display: none !important;
                    }
                    @page {
                        size: A5 landscape;
                        margin: 0;
                    }
                    body {
                        width: 210mm;
                        height: 148mm;
                    }
                }
            </style>
        </head>
        <body>
            <div class="loading">
                <div class="spinner"></div>
                <h2 style="margin-top: 20px;">Préparation de ${ordersToPrint.length} étiquettes...</h2>
            </div>
        </body>
    </html>
  `);

        // Create a temporary container in the current document
        const tempContainer = document.createElement('div');
        document.body.appendChild(tempContainer);

        // Render our component into the temporary container
        const ReactDOM = require('react-dom');

        // REMOVE THIS - Don't check for global exchange status
        // const hasExchangeOrder = isExchange || ordersToPrint.some(order => order.echange === true);
        
        // INSTEAD - Pass the isExchange prop but let BatchPrint handle individual orders
        ReactDOM.render(
            <BatchPrint
                orders={ordersToPrint}
                logo="/logo-print.png"
                mylerzLogo="/mylerz.png"
                // Don't pass a global isExchange flag - let each order be checked individually
            />,
            tempContainer
        );

        // Rest of the function remains unchanged
        setTimeout(() => {
            try {
                // Update the print window with the rendered content
                const printContent = tempContainer.innerHTML;

                if (!printWindow.document.body) {
                    toast.error("La fenêtre d'impression a été fermée");
                    toast.dismiss(loadingToastId);
                    setPrintModalOpen(false);
                    return;
                }

                // Replace content in print window
                printWindow.document.write(`
        <html>
            <head>
                <title>Étiquettes commandes (${ordersToPrint.length})</title>
                <style>
                    @page {
                        size: 148mm 210mm landscape;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    .a5-page {
                        width: 210mm;
                        page-break-after: always;
                        position: relative;
                        box-sizing: border-box;
                    }
                    .a5-page:last-child {
                        page-break-after: auto;
                    }
                </style>
            </head>
            <body>
                ${printContent}
                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                            // Uncomment to close automatically after printing
                            // window.close();
                        }, 500);
                    };
                </script>
            </body>
        </html>
      `);
                printWindow.document.close();

                // Dismiss loading toast
                toast.dismiss(loadingToastId);
                toast.success(`${ordersToPrint.length} étiquettes prêtes à imprimer`);

                // Add these two lines after the toast.success:
                refreshData();
                refreshDashboard();

            } catch (error) {
                console.error("Error updating print window:", error);
                toast.error("Erreur lors de la préparation de l'impression");
                toast.dismiss(loadingToastId);
            }

            // Clean up
            document.body.removeChild(tempContainer);
            setPrintModalOpen(false);
        }, ordersToPrint.length * 500); // Give enough time based on number of orders
    };

    // Check if any selected orders can be synchronized
    const canSynchronizeSelected = (selectedOrders) => {
        // Check if there are any selected orders first
        if (!selectedOrders || selectedOrders.length === 0) {
            return false;
        }

        // Helper function to safely get lowercase status
        const getLowerStatus = (order) => {
            if (!order || !order.status) return '';
            return typeof order.status === 'string' ? order.status.toLowerCase() : '';
        };

        // Check if all selected orders have appropriate status
        return selectedOrders.every(order => {
            const statusLower = getLowerStatus(order);
            return ['confirmé', 'confirmed', 'prêt à expédier', 'readytoship', 'readytoship', 'expédié', 'shipped'].includes(statusLower);
        });
    };

    // Add a console.log to verify these props are available
    console.log("refreshData and refreshDashboard available:", !!refreshData, !!refreshDashboard);

    // When calling the print function, pass the isExchange parameter
    const handlePrint = () => {
        if (handlePrintOrders) {
            handlePrintOrders(isExchange); // Pass isExchange to the print handler
        }
    };

    // Add this function to handle file selection and preview
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFile(file);

        // Read and preview the Excel file
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Extract barcodes from the barCode column
                const barcodes = jsonData
                    .map(row => row.barCode || row.barcode || row.BarCode || row.BARCODE)
                    .filter(barcode => barcode && barcode.toString().trim() !== '')
                    .map(barcode => barcode.toString().trim());

                setImportPreview(barcodes.slice(0, 10)); // Show first 10 for preview
                console.log('Extracted barcodes:', barcodes);
            } catch (error) {
                console.error('Error reading Excel file:', error);
                toast.error('Erreur lors de la lecture du fichier Excel');
                setSelectedFile(null);
                setImportPreview([]);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // Add this function to handle the import
    const handleImportOrders = async () => {
        if (!selectedFile) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }

        setIsImporting(true);

        try {
            // Read the Excel file
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // Extract barcodes from the barCode column
                    const barcodes = jsonData
                        .map(row => row.barCode || row.barcode || row.BarCode || row.BARCODE)
                        .filter(barcode => barcode && barcode.toString().trim() !== '')
                        .map(barcode => barcode.toString().trim());

                    if (barcodes.length === 0) {
                        toast.error('Aucun code-barres trouvé dans le fichier');
                        setIsImporting(false);
                        return;
                    }

                    console.log('Importing barcodes:', barcodes);

                    // Use the controller function instead of direct API call
                    const result = await importOrders(barcodes);
                    
                    toast.success(`${barcodes.length} commandes importées avec succès!`);
                    
                    // Refresh data
                    if (refreshData) {
                        refreshData();
                    }
                    if (refreshDashboard) {
                        refreshDashboard();
                    }

                    // Close modal and reset state
                    setIsImportModalOpen(false);
                    setSelectedFile(null);
                    setImportPreview([]);

                } catch (error) {
                    console.error('Error importing orders:', error);
                    toast.error('Erreur lors de l\'importation des commandes');
                } finally {
                    setIsImporting(false);
                }
            };
            reader.readAsArrayBuffer(selectedFile);
        } catch (error) {
            console.error('Error processing file:', error);
            toast.error('Erreur lors du traitement du fichier');
            setIsImporting(false);
        }
    };

    return (
        <>
            <div className="mb-6">
                <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-0">{pageTitle}</h1>

                {/* Mobile View: Stacked Buttons */}
                <div className="flex flex-col space-y-2 sm:hidden mt-3">
                    <button
                      disabled={!hasPermission('write')}
                        onClick={() => navigate(addNewRoute)}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none"
                    >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                        {addNewLabel}
                    </button>

                    {/* Add Import Orders Button */}
                    <button
                       disabled={!hasPermission('importExcel')}

                        onClick={openImportModal}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        Importer Excel
                    </button>

                    {/* New Print All Button */}
                    <button
                        onClick={() => openPrintModal('all')}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 focus:outline-none"
                        disabled={filteredOrders.length === 0 || !hasPermission('print')}
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Imprimer tous
                    </button>

                    {/* Print Selected Button (Only visible when there are selections) */}
                    {selectedOrders.length > 0 && (
                        <button
                        disabled={ !hasPermission('print')}

                            onClick={() => openPrintModal('selected')}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Imprimer sélection ({selectedOrders.length})
                        </button>
                    )}



                    {isDeliveredFilter && selectedOrders.length > 0 && (
                        <button
                            disabled={isMarkingPaid || !hasPermission('edit')}
                            onClick={handleMarkAsPaid}
                            className="disabled:bg-gray-400 disabled:text-gray-700 disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
                            title="Marquer les commandes livrées sélectionnées comme payées"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {isMarkingPaid ? 'En cours...' : `Marquer payé (${selectedOrders.length})`}
                        </button>
                    )}

                    <button
                        onClick={() => exportToExcel(filteredOrders)}
                        disabled={ !hasPermission('export')}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Exporter
                    </button>

                    <button
                        onClick={() => openSyncModal('all')}
                        disabled={ !hasPermission('sync')}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Synchroniser Tout
                    </button>

                    {/* Add Track Button for mobile */}
                    <button
                        onClick={() => openTrackModal('all')}
                        disabled={ !hasPermission('follow')}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        Suivre Toutes
                    </button>

                    {selectedOrders.length > 0 && (
                        <button
                        disabled={ !hasPermission('follow')}
                            onClick={() => openTrackModal('selected')}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            Suivre Sélection ({selectedOrders.length})
                        </button>
                    )}

                    {/* Modify the Sync Selection button to be disabled when no eligible orders */}
                    {selectedOrders.length > 0 && (
                        <button
                            onClick={() => openSyncModal('selected')||  !hasPermission('sync')}
                            disabled={!canSynchronizeSelected(selectedOrders)}
                            className={`disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${canSynchronizeSelected(selectedOrders)
                                ? 'text-white bg-green-600 hover:bg-green-700'
                                : 'text-gray-500 bg-gray-200 cursor-not-allowed'
                                } focus:outline-none`}
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Sync Sélection ({selectedOrders.length})
                        </button>
                    )}
                </div>

                {/* Desktop View: Row of Buttons */}
                <div className="hidden sm:flex sm:items-center sm:justify-between">
                    <div className="flex-grow"></div> {/* Spacer */}
                    <div className="flex space-x-3">
                        {/* Print All Button */}
                        <button
                            onClick={() => openPrintModal('all')}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 focus:outline-none"
                            disabled={filteredOrders.length === 0 || !hasPermission('print')}
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Imprimer tous
                        </button>

                        {/* Print Selected Button (Only visible when there are selections) */}
                        {selectedOrders.length > 0 && (
                            <button
                            disabled={ !hasPermission('print')}
                                onClick={() => openPrintModal('selected')}
                                className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Imprimer sélection ({selectedOrders.length})
                            </button>
                        )}

                        {isDeliveredFilter && selectedOrders.length > 0 && (
                            <button
                                disabled={isMarkingPaid || !hasPermission('edit')}
                                onClick={handleMarkAsPaid}
                                className="disabled:bg-gray-400 disabled:text-gray-700 disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
                                title="Marquer les commandes livrées sélectionnées comme payées"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {isMarkingPaid ? 'En cours...' : `Marquer payé (${selectedOrders.length})`}
                            </button>
                        )}



                        <button
                        disabled={ !hasPermission('sync')}
                            onClick={() => openSyncModal('all')}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                            Synchroniser Tout
                        </button>

                        {selectedOrders.length > 0 && (
                            <button
                                onClick={() => openSyncModal('selected')}
                                disabled={!canSynchronizeSelected(selectedOrders)|| !hasPermission('sync')}
                                className={`disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${canSynchronizeSelected(selectedOrders)
                                    ? 'text-white bg-green-600 hover:bg-green-700'
                                    : 'text-gray-500 bg-gray-200 cursor-not-allowed'
                                    } focus:outline-none`}
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Sync Sélection ({selectedOrders.length})
                            </button>
                        )}

                        {/* Tracking Buttons */}
                        <button
                            onClick={() => openTrackModal('all')}
                            disabled={ !hasPermission('follow')}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            Suivre Toutes
                        </button>

                        {selectedOrders.length > 0 && (
                            <button
                            disabled={ !hasPermission('follow')}
                                onClick={() => openTrackModal('selected')}
                                className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 focus:outline-none"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                Suivre Sélection ({selectedOrders.length})
                            </button>
                        )}

                        <button
                        disabled={ !hasPermission('write')}
                            onClick={() => navigate(addNewRoute)}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none"
                        >
                            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                            {addNewLabel}
                        </button>

                        {/* Add Import Orders Button */}
                        <button
                        disabled={ !hasPermission('importExcel')}
                            onClick={openImportModal}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                            Importer Excel
                        </button>

                        <button
                        disabled={ !hasPermission('export')}
                            onClick={() => exportToExcel(filteredOrders)}
                            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Exporter
                        </button>
                    </div>
                </div>
            </div>

            {(searchTerm || statusFilter !== 'all') && (
                <div className="mt-2 text-xs text-gray-500">
                    Remarque : L'export inclura uniquement les commandes actuellement filtrées ({filteredOrders.length} commandes)
                </div>
            )}

            {/* Synchronization Modal - Using content from your old code */}
            <Modal
                isOpen={isSyncModalOpen}
                onClose={() => setIsSyncModalOpen(false)}
                title="Confirmer la synchronisation"
                onSubmit={handleSynchronize}
                submitLabel="Synchroniser"
                isSubmitting={isSubmitting}
                size="md"
            >
                <div className="py-4">
                    <div className="flex items-center justify-center text-indigo-500 mb-4">
                        <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </div>

                    <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
                        {syncMode === 'selected'
                            ? `Synchroniser ${selectedOrders.length} commandes sélectionnées?`
                            : `Synchroniser toutes les commandes (${filteredOrders.length})?`}
                    </h3>

                    <p className="text-sm text-gray-600 text-center mb-4">
                        Cette action synchronisera les commandes avec le système externe. Voulez-vous continuer?
                    </p>

                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                        <div className="flex">
                            <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="ml-3">
                                <p className="text-sm text-amber-700">
                                    {syncMode === 'selected'
                                        ? "Seules les commandes sélectionnées seront synchronisées."
                                        : "Toutes les commandes actuellement filtrées seront synchronisées."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Print Confirmation Modal */}
            <Modal
                isOpen={isPrintModalOpen}
                onClose={() => setPrintModalOpen(false)}
                title="Confirmer l'impression"
                onSubmit={handleBatchPrint}
                submitLabel="Imprimer"
                size="md"
            >
                <div className="py-4">
                    <div className="flex items-center justify-center text-rose-500 mb-4">
                        <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                    </div>

                    <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
                        {printMode === 'selected'
                            ? `Imprimer ${selectedOrders.length} étiquettes sélectionnées?`
                            : `Imprimer toutes les étiquettes (${filteredOrders.length})?`}
                    </h3>

                    <p className="text-sm text-gray-600 text-center mb-4">
                        Cette action générera un document d'impression avec une étiquette par page. Voulez-vous continuer?
                    </p>

                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                        <div className="flex">
                            <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="ml-3">
                                <p className="text-sm text-amber-700">
                                    {printMode === 'selected'
                                        ? "Seules les commandes sélectionnées seront imprimées."
                                        : "Toutes les commandes actuellement filtrées seront imprimées."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Add new Tracking Modal */}
            <Modal
                isOpen={isTrackModalOpen}
                onClose={() => setIsTrackModalOpen(false)}
                title="Confirmer le suivi"
                onSubmit={handleConfirmTracking}
                submitLabel="Suivre"
                isSubmitting={isTrackingSubmitting} // Use the new state variable here
                size="md"
            >
                <div className="py-4">
                    <div className="flex items-center justify-center text-teal-500 mb-4">
                        <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                    </div>

                    <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
                        {trackMode === 'selected'
                            ? `Suivre ${selectedOrders.length} commandes sélectionnées?`
                            : `Suivre toutes les commandes (${filteredOrders.length})?`}
                    </h3>

                    <p className="text-sm text-gray-600 text-center mb-4">
                        Cette action permettra de suivre les commandes sélectionnées. Voulez-vous continuer?
                    </p>

                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                        <div className="flex">
                            <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="ml-3">
                                <p className="text-sm text-amber-700">
                                    {trackMode === 'selected'
                                        ? "Seules les commandes sélectionnées seront suivies."
                                        : "Toutes les commandes actuellement filtrées seront suivies."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Import Orders Modal */}
            <Modal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                title="Importer des commandes"
                onSubmit={handleImportOrders}
                submitLabel="Importer"
                isSubmitting={isImporting}
                size="lg"
            >
                <div className="py-4">
                    <div className="flex items-center justify-center text-purple-500 mb-4">
                        <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                    </div>

                    <h3 className="text-lg font-medium text-center text-gray-900 mb-4">
                        Sélectionner un fichier Excel
                    </h3>

                    <div className="mb-4">
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileSelect}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                        />
                    </div>

                    {importPreview.length > 0 && (
                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Aperçu des codes-barres ({importPreview.length} premiers):
                            </h4>
                            <div className="bg-gray-50 rounded-md p-3 max-h-32 overflow-y-auto">
                                {importPreview.map((barcode, index) => (
                                    <div key={index} className="text-xs text-gray-600 font-mono">
                                        {barcode}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                        <div className="flex">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    Le fichier Excel doit contenir une colonne nommée "barCode" avec les codes-barres des commandes à importer.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default OrdersHeader;